import React, { Component } from 'react';
import { Container, Row, Col, Badge, Button } from 'reactstrap';

class Gallery extends Component {
    constructor(props) {
        super(props);

        this.state = {
            gallery: null,
            siteSettings: null,
            footerSvgContent: '',
            footerAltText: '',
            loading: true,
            error: null,
        };
    }

    componentDidMount() {
        this.loadGallery();
        this.loadSiteSettings();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.slug !== this.props.slug) {
            this.loadGallery();
        }
    }

    loadGallery = () => {
        const { slug } = this.props;

        if (!slug) {
            this.setState({
                gallery: null,
                loading: false,
                error: 'No gallery category was provided in the URL.',
            });
            return;
        }

        this.setState({
            gallery: null,
            loading: true,
            error: null,
        });

        fetch(
            `${process.env.REACT_APP_API_URL}/api/galleries?where[slug][equals]=${encodeURIComponent(
                slug
            )}&depth=1&limit=1`
        )
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                return res.json();
            })
            .then((data) => {
                const gallery = data?.docs?.[0] || null;

                if (!gallery) {
                    this.setState({
                        gallery: null,
                        loading: false,
                        error: `Gallery "${slug}" was not found.`,
                    });
                    return;
                }

                this.setState({
                    gallery,
                    loading: false,
                    error: null,
                });
            })
            .catch((error) => {
                console.error('Gallery fetch error:', error);
                this.setState({
                    gallery: null,
                    loading: false,
                    error: error.message || 'Failed to load gallery.',
                });
            });
    };

    loadSiteSettings = () => {
        fetch(`${process.env.REACT_APP_API_URL}/api/globals/site-settings?depth=1`)
            .then(async (res) => {
                if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                const data = await res.json();

                const footerLogo = data.footerLogo;
                let footerSvgContent = '';

                if (footerLogo?.url) {
                    try {
                        const svgRes = await fetch(footerLogo.url);
                        if (svgRes.ok) {
                            footerSvgContent = await svgRes.text();
                        } else {
                            console.warn('Footer SVG fetch failed:', svgRes.status);
                        }
                    } catch (err) {
                        console.error('Footer SVG fetch error:', err);
                    }
                }

                this.setState({
                    siteSettings: data,
                    footerSvgContent,
                    footerAltText: footerLogo?.altText || 'Footer Logo',
                });
            })
            .catch((error) => {
                console.error('Site settings fetch error:', error);
            });
    };

    getMediaUrl = (mediaObj) => {
        if (!mediaObj) return '';

        // Prefer the stored url (S3 or absolute) — fall back to constructing
        // from filename only if url is absent.
        if (mediaObj.url) {
            return mediaObj.url;
        }

        if (mediaObj.filename) {
            return `${process.env.REACT_APP_API_URL}/api/media/file/${mediaObj.filename}`;
        }

        return '';
    };

    render() {
        const {
            gallery,
            footerSvgContent,
            footerAltText,
            loading,
            error,
        } = this.state;

        if (loading) {
            return (
                <Container className="my-5">
                    <p className="text-center">Loading gallery...</p>
                </Container>
            );
        }

        if (error) {
            return (
                <Container className="my-5">
                    <p className="text-center text-danger">Error: {error}</p>
                </Container>
            );
        }

        if (!gallery) {
            return (
                <Container className="my-5">
                    <p className="text-center">No gallery found.</p>
                </Container>
            );
        }

        const images = gallery.images || [];
        const tags = gallery.tags || [];

        return (
            <Container className="my-5 mb-4">
                <Row>
                    <Col xs={12}>
                        <div style={{ textAlign: 'center' }}>
                            <h1>{gallery.name}</h1>

                            {gallery.description && <p>{gallery.description}</p>}

                            {tags.length > 0 && (
                                <div style={{ marginTop: '1rem' }}>
                                    {tags.map((tagItem, index) => (
                                        <Badge key={index} pill style={{ margin: '0.25rem' }}>
                                            {tagItem.tag}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Col>

                    <Col>
                        <Row>
                            <div className="bg-primary mt-4 mb-3" style={{ height: '1px' }} />
                        </Row>
                    </Col>
                </Row>

                <Row className="mb-4 mt-5">
                    <Col>
                        <div style={{ textAlign: 'center' }}>
                            <h1>Our Products</h1>
                        </div>
                    </Col>
                </Row>

                {images.length === 0 ? (
                    <Row>
                        <Col xs={12}>
                            <p className="text-center">This gallery has no images yet.</p>
                        </Col>
                    </Row>
                ) : (
                    <Row>
                        {images.map((item, index) => {
                            const imageUrl = this.getMediaUrl(item.image);
                            const itemId = item.id || item._id;
                            const detailHref = itemId
                                ? `/gallery/${gallery.slug}/${itemId}`
                                : null;

                            const tileContent = (
                                <div style={{ overflow: 'hidden', height: '100%' }}>
                                    {imageUrl ? (
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '260px',
                                                overflow: 'hidden',
                                                backgroundColor: '#f8f9fa',
                                            }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt={item.title || gallery.name || 'Gallery image'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '260px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#777',
                                            }}
                                        >
                                            No image
                                        </div>
                                    )}

                                    {(item.title || item.description) && (
                                        <div className="mt-3">
                                            {item.title && (
                                                <h5
                                                    style={{
                                                        marginBottom: '0.5rem',
                                                        color: 'inherit',
                                                    }}
                                                >
                                                    {item.title}
                                                </h5>
                                            )}
                                            {item.description && (
                                                <p
                                                    style={{
                                                        margin: 0,
                                                        color: '#666',
                                                        lineHeight: '1.5',
                                                    }}
                                                >
                                                    {item.description.length > 80
                                                        ? item.description.slice(0, 80) + '...'
                                                        : item.description}
                                                </p>
                                            )}
                                            <Button color="primary" size="sm" className="mt-2">More...</Button>
                                        </div>
                                    )}
                                </div>
                            );

                            return (
                                <Col key={itemId || index} lg={3} md={4} sm={6} xs={12} className="mb-4">
                                    {detailHref ? (
                                        <a
                                            href={detailHref}
                                            style={{
                                                textDecoration: 'none',
                                                color: 'inherit',
                                                display: 'block',
                                                height: '100%',
                                            }}
                                        >
                                            {tileContent}
                                        </a>
                                    ) : (
                                        tileContent
                                    )}
                                </Col>
                            );
                        })}
                    </Row>
                )}

                {footerSvgContent && (
                    <>
                        <Row className="mt-5 mb-4">
                            <Col>
                                <div className="bg-primary" style={{ height: '1px' }} />
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col xs={12}>
                                <div style={{ textAlign: 'center' }}>
                                    <div
                                        aria-label={footerAltText}
                                        dangerouslySetInnerHTML={{ __html: footerSvgContent }}
                                        style={{
                                            maxWidth: '200px',
                                            margin: '0 auto',
                                            opacity: 0.9,
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        );
    }
}

export default Gallery;